import React, { useContext, useEffect, useState } from "react"

import Paper from "@mui/material/Paper"
import Container from "@mui/material/Container"
import Grid from "@mui/material/Grid"
import { makeStyles } from "@mui/styles"
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import ForumIcon from "@mui/icons-material/Forum";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonIcon from "@mui/icons-material/Person";
import TimerIcon from "@mui/icons-material/Timer";
import WarningIcon from "@mui/icons-material/Warning";

import useTickets from "../../hooks/useTickets"
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import Chart from "./Chart"
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 240,
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
  },
  statCard: {
    padding: theme.spacing(3),
    display: "flex",
    overflow: "hidden",
    flexDirection: "column",
    height: 130,
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    position: "relative",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    },
  },
  cardAttendance: { borderTop: `4px solid ${theme.custom.chartPalette[0]}` },
  cardWaiting:    { borderTop: `4px solid ${theme.custom.chartPalette[1]}` },
  cardClosed:     { borderTop: `4px solid ${theme.custom.chartPalette[2]}` },
  cardPersonal:   { borderTop: `4px solid ${theme.custom.chartPalette[0]}` },
  cardGeneral:    { borderTop: `4px solid ${theme.custom.chartPalette[1]}` },
  cardToday:      { borderTop: `4px solid ${theme.custom.chartPalette[3]}` },
  cardStale:      { borderTop: `4px solid ${theme.custom.chartPalette[4]}` },
  cardTeam: {
    padding: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
  },
  cardIcon: {
    position: "absolute",
    right: 20,
    top: 20,
    opacity: 0.15,
    fontSize: 48,
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: theme.spacing(1),
  },
  cardNumber: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  teamRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  teamName: {
    flex: 1,
    fontWeight: 500,
    marginLeft: 10,
  },
  teamCount: {
    fontWeight: 700,
    color: "#F37021",
    marginRight: 12,
    fontSize: "1.1rem",
  },
  teamAvg: {
    color: "#8A9096",
    fontSize: "0.8rem",
  },
  teamHeader: {
    fontWeight: 700,
    color: "#273A74",
    marginBottom: theme.spacing(1.5),
  },
}))

const today = new Date().toISOString().slice(0, 10);

const Dashboard = () => {
  const classes = useStyles()
  const { user } = useContext(AuthContext);
  const isPrivileged = user.profile === "admin" || user.profile === "supervisor";
  const [agentStats, setAgentStats] = useState(null);
  const [staleCount, setStaleCount] = useState(0);

  const userQueueIds = user.queues && user.queues.length > 0 ? user.queues.map(q => q.id) : [];

  // Card 1: Minha Fila (agentes) ou Em Atendimento geral (admin/supervisor)
  const { count: personalCount } = useTickets({
    status: "open",
    showAll: isPrivileged ? "true" : "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  // Card 2: Fila Geral (pending sem userId)
  const { count: generalCount } = useTickets({
    status: "pending",
    showAll: "true",
    unassigned: "true",
    queueIds: JSON.stringify(userQueueIds),
  });

  // Card 3: Resolvidos Hoje
  const { count: closedTodayCount } = useTickets({
    status: "closed",
    showAll: "true",
    date: today,
    queueIds: JSON.stringify(userQueueIds),
  });

  useEffect(() => {
    if (!isPrivileged) return;
    api.get("/tickets/stats/today")
      .then(({ data }) => setAgentStats(data))
      .catch(() => {});
    api.get("/tickets/stale")
      .then(({ data }) => setStaleCount(data.count || 0))
      .catch(() => {});
  }, [isPrivileged]);

  return (
    <div>
      <Container maxWidth="lg" className={classes.container}>
        <Grid container spacing={3}>
          {/* Card 1 */}
          <Grid item xs={12} sm={4}>
            <Paper className={`${classes.statCard} ${isPrivileged ? classes.cardAttendance : classes.cardPersonal}`}>
              {isPrivileged
                ? <HeadsetMicIcon className={classes.cardIcon} style={{ fontSize: 64, color: "#F37021" }} />
                : <PersonIcon className={classes.cardIcon} style={{ fontSize: 64, color: "#F37021" }} />
              }
              <Typography className={classes.cardLabel}>
                {isPrivileged
                  ? i18n.t("dashboard.messages.inAttendance.title")
                  : i18n.t("dashboard.messages.personal.title")}
              </Typography>
              <Typography component="h1" variant="h3" className={classes.cardNumber}>
                {personalCount}
              </Typography>
            </Paper>
          </Grid>

          {/* Card 2: Fila Geral */}
          <Grid item xs={12} sm={4}>
            <Paper className={`${classes.statCard} ${classes.cardGeneral}`}>
              <ForumIcon className={classes.cardIcon} style={{ fontSize: 64, color: "#0071BC" }} />
              <Typography className={classes.cardLabel}>
                {i18n.t("dashboard.messages.general.title")}
              </Typography>
              <Typography component="h1" variant="h3" className={classes.cardNumber}>
                {generalCount}
              </Typography>
            </Paper>
          </Grid>

          {/* Card 3: Resolvidos Hoje */}
          <Grid item xs={12} sm={4}>
            <Paper className={`${classes.statCard} ${classes.cardToday}`}>
              <CheckCircleOutlineIcon className={classes.cardIcon} style={{ fontSize: 64, color: "#4CAF50" }} />
              <Typography className={classes.cardLabel}>
                {i18n.t("dashboard.messages.resolvedToday.title")}
              </Typography>
              <Typography component="h1" variant="h3" className={classes.cardNumber}>
                {isPrivileged && agentStats ? agentStats.closedToday : closedTodayCount}
              </Typography>
              {agentStats && agentStats.avgMinutes > 0 && (
                <Typography variant="caption" style={{ color: "#8A9096", marginTop: 4 }}>
                  {`Tempo médio: ${agentStats.avgMinutes} min`}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Card 4: Aguardando +1h — só admin/supervisor */}
          {isPrivileged && (
            <Grid item xs={12} sm={4}>
              <Paper className={`${classes.statCard} ${classes.cardStale}`}>
                <WarningIcon className={classes.cardIcon} style={{ fontSize: 64, color: "#E74C3C" }} />
                <Typography className={classes.cardLabel}>
                  Aguardando +1h
                </Typography>
                <Typography component="h1" variant="h3" className={classes.cardNumber}
                  style={{ color: staleCount > 0 ? "#E74C3C" : undefined }}>
                  {staleCount}
                </Typography>
                <Typography variant="caption" style={{ color: "#8A9096", marginTop: 4 }}>
                  Clientes sem atendimento
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Gráfico horário */}
          <Grid item xs={12}>
            <Paper className={classes.fixedHeightPaper}>
              <Chart />
            </Paper>
          </Grid>

          {/* Desempenho da equipe — só para admin/supervisor */}
          {isPrivileged && agentStats && agentStats.byAgent && agentStats.byAgent.length > 0 && (
            <Grid item xs={12}>
              <Paper className={classes.cardTeam}>
                <Typography variant="subtitle1" className={classes.teamHeader}>
                  {i18n.t("dashboard.messages.teamPerformance.title")}
                </Typography>
                {agentStats.byAgent.map((agent, idx) => (
                  <div key={agent.userId ?? idx} className={classes.teamRow}>
                    <Avatar style={{ width: 32, height: 32, fontSize: 14, background: "#F37021" }}>
                      {agent.userName ? agent.userName[0].toUpperCase() : "?"}
                    </Avatar>
                    <Typography className={classes.teamName}>{agent.userName}</Typography>
                    <Typography className={classes.teamCount}>{agent.count} resolvidos</Typography>
                    <Typography className={classes.teamAvg}>
                      <TimerIcon style={{ fontSize: 12, verticalAlign: "middle", marginRight: 2 }} />
                      {agent.avgMinutes} min
                    </Typography>
                  </div>
                ))}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  )
}

export default Dashboard
